import { useState, useEffect } from "react";
import { Radio, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Editor-only preview button: injects a fake 60-second "active" broadcast
 * so the editor can see the red LiveNowBanner flash across her own devices
 * without actually going live on Larix and without spamming members.
 */
export const TestLivePing = () => {
  const [firing, setFiring] = useState(false);
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (remaining <= 0) return;
    const t = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [remaining]);

  const fire = async () => {
    if (firing || remaining > 0) return;
    setFiring(true);
    try {
      const { data } = await api.post("/live/test-ping");
      setRemaining(data.expires_in_seconds || 60);
      toast.success("Test ping fired ✨", {
        description: "Open the app on your phone — the red LIVE banner should appear within 30s and stay for ~60s.",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to fire test ping");
    } finally {
      setFiring(false);
    }
  };

  const active = remaining > 0;

  return (
    <section
      data-testid="test-live-ping-panel"
      style={{
        border: "1px solid var(--border-default)",
        background: "var(--surface)",
        padding: 24,
        marginBottom: 32,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
        <div style={{ maxWidth: 520 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <Sparkles size={16} strokeWidth={1.5} color="var(--brand-accent)" />
            <span
              style={{
                fontSize: 10,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 600,
                color: "var(--brand-accent)",
              }}
            >
              Preview only
            </span>
          </div>
          <h2 className="font-serif" style={{ fontSize: 22, marginBottom: 6 }}>
            Test the Live banner
          </h2>
          <p style={{ fontSize: 14, lineHeight: 1.55, color: "var(--text-secondary)" }}>
            Fires a fake 60-second live broadcast so you can watch the red banner
            flash across every page on your phone and desktop. Members receive
            no push, and the fake broadcast is auto-deleted after 60 seconds —
            it never appears in any archive or feed.
          </p>
        </div>
        <button
          type="button"
          data-testid="fire-test-ping-button"
          onClick={fire}
          disabled={firing || active}
          className="btn-accent"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 20px",
            fontSize: 12,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontWeight: 600,
            opacity: firing || active ? 0.55 : 1,
            cursor: firing || active ? "wait" : "pointer",
          }}
        >
          <Radio size={14} strokeWidth={1.8} />
          {active ? `Live for ${remaining}s` : firing ? "Firing…" : "Fire test ping"}
        </button>
      </div>
    </section>
  );
};

export default TestLivePing;
